<template>
  <model-form
    ref="modelForm"
    :model="screen"
    modal-type="modal"
  >
    <div class="column">
      <div class="row q-col-gutter-sm items-start">
        <q-input
          v-model="screen.name"
          class="col-6"
          label="Name"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
          :autofocus="!$q.platform.is.ios"
        />
        <div class="col-6 q-pt-sm row">
          <q-btn
            class="col"
            :color="screen.color"
            glossy
            unelevated
            :style="'background-color: ' + screen.color"
            size="lg"
          >
            <div class="text-caption text-weight-light">
              <div class="text-capitalize">
                {{ screen.color }}
              </div>
            </div>
            <q-menu
              anchor="top left"
              self="top left"
            >
              <q-color v-model="screen.color" />
            </q-menu>
          </q-btn>
        </div>
      </div>
      <div class="row q-col-gutter-sm items-start">
        <model-form-select
          field="locationGroup"
          class="col"
          label="Location Group"
          :options="locationGroups.data"
          option-label="name"
          clearable
        />
      </div>
      <div class="row q-col-gutter-sm items-start">
        <showing-badge-select
          :model="screen"
          class="col"
        />
      </div>
      <div class="row q-col-gutter-sm q-mt-sm">
        <q-toggle
          v-model="screen.overridePriceCard"
          class="col-6 q-pb-md"
          label="Custom Price Card"
        />
        <q-select
          v-model="screen.customPriceCard"
          :disable="!screen.overridePriceCard"
          class="col-6"
          label="Price Card"
          :options="$_.filter(priceCards.data, priceCard => !priceCard.isArchived)"
          option-label="name"
          :display-value="screen.overridePriceCard ? screen.customPriceCard && screen.customPriceCard.name : ''"
          bottom-slots
        />
      </div>
      <div class="row q-col-gutter-sm items-start">
        <q-select
          v-model="screen.seatChart"
          class="col-8"
          label="Seat Chart"
          :options="seatChartOptions"
          :option-label="(option) => option ? option.name : 'None'"
          :display-value="screen.seatChart ? screen.seatChart.name : 'None'"
          clearable
          @update:model-value="handleSeatChartUpdate"
        >
          <q-tooltip>
            When a seat chart is selected and reserve seating is not enabled, tickets are limited to
            <br>
            the types specified on the seat chart. When reserve seating is enabled, customers may
            <br>
            choose their own seats.
          </q-tooltip>
        </q-select>
        <q-checkbox
          v-model="screen.reservedSeating"
          class="col-4 q-pb-md"
          label="Reserve Seating"
          :disable="!screen.seatChart"
          bottom-slots
        />
      </div>
      <div
        class="row q-col-gutter-sm items-center q-mt-sm"
      >
        <q-input
          v-model.number="screen.seatCount"
          type="number"
          min="0"
          max="1000"
          class="col-4"
          label="Seat Count"
          :rules="[val => screen.reservedSeating || val === 0 || !!val || 'Field is required']"
          :disable="!!screen.seatChart"
          bottom-slots
        />
        <q-input
          v-model.number="screen.heldSeatCount"
          type="number"
          min="0"
          :max="screen.seatCount"
          class="col-4"
          label="Held Seat Count"
          :rules="[val => val !== '' || 'Field is required', val => val >= 0 || 'Cannot be less than 0', val => val <= screen.seatCount || 'Must be less than total seats']"
          :disable="!!screen.seatChart"
          bottom-slots
        />
        <q-input
          v-model.number="screen.marathonSeatCount"
          type="number"
          min="0"
          :max="screen.seatCount - screen.heldSeatCount"
          class="col-4"
          label="Marathon Seat Count"
          :rules="[val => val !== '' || 'Field is required', val => val >= 0 || 'Cannot be less than 0', val => val <= (screen.seatCount - screen.heldSeatCount) || 'Must be less than total less held seats']"
          :disable="!!screen.seatChart"
          bottom-slots
        />
      </div>
      <div class="row q-col-gutter-sm items-start">
        <q-input
          v-model.number="screen.preStartTimeBufferMinutes"
          type="number"
          min="0"
          max="100"
          class="col-4"
          label="Pre-start time buffer (minutes)"
          :rules="[val => !!val || val === 0 || 'Field is required']"
          bottom-slots
        />
        <q-input
          v-model.number="screen.postStartTimeBufferMinutes"
          type="number"
          min="0"
          max="100"
          class="col-4"
          label="Post-start time buffer (minutes)"
          :rules="[val => !!val || val === 0 || 'Field is required']"
          bottom-slots
        />
        <q-input
          v-model.number="screen.postShowingBufferMinutes"
          type="number"
          min="0"
          max="100"
          class="col-4"
          label="Post-showing buffer (minutes)"
          :rules="[val => !!val || val === 0 || 'Field is required']"
          bottom-slots
        />
      </div>
      <div class="row q-col-gutter-sm">
        <q-toggle
          v-model="screen.overrideMaxTicketsPerOrderPerShowing"
          class="col-6 q-pb-md"
          label="Override Max Tickets Per Order Per Showing"
        />
        <q-input
          v-model.number="screen.maxTicketsPerOrderPerShowing"
          :disable="!screen.overrideMaxTicketsPerOrderPerShowing"
          type="number"
          min="0"
          step="1"
          class="col-6"
          label="Max Tickets Per Order Per Showing"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
        />
      </div>
    </div>
  </model-form>
</template>

<script>

import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import ShowingBadgeSelect from 'src/modules/admin/showing-badge/components/ShowingBadgeSelect.vue'

export default {
  name: 'ScreenForm',
  components: {
    ModelForm,
    ShowingBadgeSelect
  },
  configuration: ['locationGroups', 'priceCards', 'showingBadges'],
  computed: {
    seatChartOptions () {
      const seatCharts = this.seatCharts.data.map((seatChart) => {
        return { id: seatChart.id, name: seatChart.name, seatCount: seatChart.seatCount }
      })

      // Include an option for an unassigned seat chart
      return [null, ...seatCharts]
    }
  },
  models: {
    screen: {
      customPriceCard: true,
      registerType: true,
      showingBadges: true,
      seatChart: true,
      libraryChildSiteIds: true
    },
    seatCharts: {
      fields: ['id', 'name', 'seatCount'],
      variables () {
        return {
          limit: 100
        }
      }
    }
  },
  methods: {
    handleSeatChartUpdate (seatChart) {
      // Disable reserved seating if a seat chart is not selected
      if (!seatChart) {
        this.screen.reservedSeating = false
        return
      }

      this.screen.seatCount = seatChart.seatCount
      this.screen.heldSeatCount = 0
      this.screen.marathonSeatCount = 0
    }
  }
}

</script>
